import { lazy, Suspense, useEffect, useRef } from "react";
import { setupInterceptors } from "services";
import { Routes, Route } from "react-router-dom";
import { useSelector } from "react-redux";
import { QueryClient, QueryClientProvider } from "react-query";
import styled, { ThemeProvider } from "styled-components/macro";
import RequireAuth from "helpers/RequireAuth";
import RedirectSignedUser from "helpers/RedirectSignedUser";
import RedirectMap from "helpers/RedirectMap";
import RedirectMapAsset from "helpers/RedirectMapAsset";
import { GlobalStyle } from "features/theme/globalStyle";
import { getThemeByName } from "config/dashboard";
import Toaster from "components/toaster/Toaster";
import { selectTheme } from "features/theme/themeSlice";
import Fallback from "components/Fallback";
import { useKeycloak } from "@react-keycloak/web";
import AllEntitiesDashboard from "features/dashboard/AllEntitiesDashboard";

const SettingsUpdate = lazy(() =>
  import("features/settings/settings.update/SettingsUpdate")
);
const SettingsCare = lazy(() =>
  import("features/settings/settings.care/SettingsCare")
);

const Assets = lazy(() => import("features/asset/assets/Assets"));
const Screen = lazy(() => import("features/asset/screen/Screen"));
const Input = lazy(() => import("features/asset/input/Input"));
const Collection = lazy(() =>
  import("features/asset/collection/settings/Settings")
);

const DiscoveriesTable = lazy(() =>
  import("features/discoveries/discoveries.table/DiscoveriesTable")
);
const DiscoveriesTableArchived = lazy(() =>
  import("features/discoveries/discoveries.table/DiscoveriesTableArchived")
);
const DiscoveriesAttributesTable = lazy(() =>
  import(
    "features/discoveries/discoveries.attributes.table/DiscoveriesAttributesTable"
  )
);

const Connector = lazy(() => import("features/connector/Connector"));
const ConnectorOverview = lazy(() =>
  import("features/connector/overview/Overview")
);
const Archived = lazy(() => import("features/asset/archived/Archived"));
const AssetOverview = lazy(() => import("features/asset/overview/Overview"));
const AssetGraph = lazy(() => import("features/asset/graph/AssetGraph"));
const SetupCreateEntity = lazy(() => import("features/setup/CreateEntity"));
const SetupAddinputs = lazy(() => import("features/setup/AddInputs"));
const SetupEnableModules = lazy(() => import("features/setup/EnableModules"));
const SetupSuccess = lazy(() => import("features/setup/Success"));
const NotFound = lazy(() => import("components/NotFound"));

const Issue = lazy(() => import("features/issue/Issue"));
const IssueModules = lazy(() => import("features/issue/modules/Modules"));
const IssueModuleVulnscanAuditor = lazy(() =>
  import("features/issue/modules/vulnscan.auditor/Overview")
);
const IssueModuleSecurityController = lazy(() =>
  import("features/issue/modules/security.controller/Overview")
);
const IssueModuleWebchangeProfiler = lazy(() =>
  import("features/issue/modules/webchange.profiler/Overview")
);
const IssueModuleCVEInspector = lazy(() =>
  import("features/issue/modules/cve.inspector/Overview")
);
const IssueOverview = lazy(() =>
  import("features/issue/overview/IssueOverview")
);
const Dashboard = lazy(() => import("features/dashboard/Dashboard"));
const TopNavbarFull = lazy(() => import("features/navbar/FullNavbar"));

const Content = styled.div`
  height: calc(100% - 60px);
`;

const queryClient = new QueryClient();

const App = () => {
  const { keycloak, initialized } = useKeycloak();
  const theme = getThemeByName(useSelector(selectTheme));

  // Interceptors added only once
  const interceptorsSet = useRef(false);
  useEffect(() => {
    if (!initialized || !keycloak) return;
    if (interceptorsSet.current) return;
    setupInterceptors(keycloak);
    interceptorsSet.current = true;
  }, [initialized, keycloak]);

  // Configure React Query
  useEffect(() => {
    queryClient.setDefaultOptions({
      queries: {
        retry: (_count, error: any) =>
          error?.response?.status === 401 ? false : true,
        onError: (error: any) => {
          if (error?.response?.status === 401) {
            console.warn("React Query 401 detected");
            queryClient.clear();
          }
        },
      },
    });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <GlobalStyle />
        <Toaster />

        <Routes>
          <Route
            path="/"
            element={
              <Suspense fallback={<Fallback />}>
                <RedirectSignedUser>
                  <Fallback />
                </RedirectSignedUser>
              </Suspense>
            }
          />

          <Route
            path="/oidc_callback"
            element={
              <Suspense fallback={<Fallback />}>
                <RedirectSignedUser />
              </Suspense>
            }
          />

          <Route
            path="/setup/create-entity"
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth requiredRole>
                  <SetupCreateEntity />
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/setup/add-inputs"
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <SetupAddinputs />
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/setup/enable-modules"
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <SetupEnableModules />
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/setup/success"
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <SetupSuccess />
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/dashboard"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <RedirectMap>
                    <Content>
                      <TopNavbarFull className="sticky" />
                      <Dashboard />
                    </Content>
                  </RedirectMap>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/dashboard/all"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <AllEntitiesDashboard />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/assets"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <RedirectMap>
                    <Content>
                      <TopNavbarFull className="sticky" />
                      <Assets />
                    </Content>
                  </RedirectMap>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/assets/screens"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <RedirectMap>
                    <Content>
                      <TopNavbarFull className="sticky" />
                      <Screen />
                    </Content>
                  </RedirectMap>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/assets/inputs"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <Input />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/assets/archived"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <Archived />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/assets/collections"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <Collection />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/settings/update"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth requiredRole>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <SettingsUpdate />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/settings/care"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <SettingsCare />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />

          <Route
            path="/assets/overview"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <RedirectMapAsset>
                    <Content>
                      <TopNavbarFull className="sticky" />
                      <AssetOverview />
                    </Content>
                  </RedirectMapAsset>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/discoveries"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <DiscoveriesTable />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/discoveries/archived"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <DiscoveriesTableArchived />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/discoveries/attributes"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <DiscoveriesAttributesTable />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/connectors"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <Connector />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/connectors/overview"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <ConnectorOverview />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/assets/graph"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <RedirectMap>
                    <Content>
                      <TopNavbarFull className="sticky" />
                      <AssetGraph />
                    </Content>
                  </RedirectMap>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/issues"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <Issue />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/issues/modules"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <IssueModules />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/issues/modules/security-controller"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <IssueModuleSecurityController />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/issues/modules/vulnscan-auditor"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <IssueModuleVulnscanAuditor />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/issues/modules/webchange-profiler"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <IssueModuleWebchangeProfiler />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/issues/modules/cve-inspector"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <IssueModuleCVEInspector />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/issues/overview"
            exact
            element={
              <Suspense fallback={<Fallback />}>
                <RequireAuth>
                  <Content>
                    <TopNavbarFull className="sticky" />
                    <IssueOverview />
                  </Content>
                </RequireAuth>
              </Suspense>
            }
          />

          <Route
            path="*"
            element={
              <Suspense fallback={<Fallback />}>
                <NotFound />
              </Suspense>
            }
          />
        </Routes>
      </QueryClientProvider>
    </ThemeProvider>
  );
};
export default App;
